import type {
  AssetPurpose,
  ViewerAsset,
  ViewerContentType,
  ViewerDataSource,
  ViewerManifest,
} from '@genesisframeworks/viewer-plugin';
import { getApiBase, getAuthHeaders } from '@services/api/apiConfig';

export interface AuthenticatedBoxDataSourceOptions {
  folderPath: string;
  filePath?: string;
  fetchImpl?: typeof fetch;
}

function absoluteApiUrl(ref: string): string {
  try {
    return new URL(ref, `${getApiBase()}/`).toString();
  } catch {
    return ref;
  }
}

async function responseError(response: Response, label: string): Promise<Error> {
  const body = await response.text().catch(() => '');
  return new Error(`${label}: HTTP ${response.status}${body ? ` ${body.slice(0, 200)}` : ''}`);
}

/**
 * Owner adapter: every asset is fetched with the same Firebase and
 * org/connection headers as the manifest. The plugin turns returned Blobs
 * into instance-owned object URLs and revokes them on mode change/unmount.
 */
export class AuthenticatedBoxDataSource implements ViewerDataSource {
  private readonly folderPath: string;
  private readonly filePath?: string;
  private readonly fetchImpl: typeof fetch;

  constructor(options: AuthenticatedBoxDataSourceOptions) {
    this.folderPath = options.folderPath;
    this.filePath = options.filePath;
    this.fetchImpl = options.fetchImpl ?? fetch.bind(globalThis);
  }

  async loadManifest(options: {
    contentType: ViewerContentType;
    signal: AbortSignal;
  }): Promise<ViewerManifest | unknown> {
    if (options.contentType === 'pdf' && this.filePath) {
      const name = decodeURIComponent(this.filePath.split('/').pop() || 'Document.pdf');
      const url = new URL('/api/files/download', `${getApiBase()}/`);
      url.searchParams.set('path', this.filePath);
      return {
        name,
        root: {
          id: 'root',
          name,
          type: 'folder',
          children: [{ id: `owner-file:${this.filePath}`, name, title: name, path: url.toString() }],
        },
      };
    }
    const url = new URL('/api/files', `${getApiBase()}/`);
    url.searchParams.set('path', this.folderPath);
    url.searchParams.set('format', 'manifest');
    url.searchParams.set('type', options.contentType);
    const response = await this.fetchImpl(url, {
      signal: options.signal,
      headers: await getAuthHeaders(),
    });
    if (!response.ok) throw await responseError(response, 'Owner manifest fetch failed');
    return response.json();
  }

  async loadAsset(
    ref: string,
    options: { purpose: AssetPurpose; signal: AbortSignal },
  ): Promise<ViewerAsset> {
    const response = await this.fetchImpl(absoluteApiUrl(ref), {
      signal: options.signal,
      headers: await getAuthHeaders(),
    });
    if (!response.ok) throw await responseError(response, 'Owner asset fetch failed');
    return { kind: 'blob', blob: await response.blob() };
  }
}

/** Backwards-compatible factory name for callers and focused tests. */
export function ownerDataSource(options: AuthenticatedBoxDataSourceOptions): ViewerDataSource {
  return new AuthenticatedBoxDataSource(options);
}
